/**
 * PressLogos component — Seamless infinite-scroll marquee of backer logos.
 *
 * iOS Safari fixes:
 *  - Each logo has an explicit `width` style so Safari doesn't collapse flex items.
 *  - The animation uses `translateX` instead of `translate3d` for better
 *    compatibility with `-webkit-overflow-scrolling`.
 *  - `will-change: transform` + `backface-visibility: hidden` for GPU compositing.
 *  - Logos are duplicated 3× to ensure the strip is always wider than the viewport.
 */

interface PressLogosProps {
  backgroundColor?: string;
}

const PressLogos = ({ backgroundColor = 'rgb(243, 241, 236)' }: PressLogosProps) => {
  const logos = [
    { name: "Backer 1", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/MKbKSSgQgsolgHcB.png" },
    { name: "Backer 2", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/VvGOfvOSjHkzYurH.png" },
    { name: "W3B Lab Amsterdam", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/pzFtJwwfYoJflyhN.png" },
    { name: "Backer 3", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/QKeRXkPqZgvAlUvI.png" },
    { name: "Generous Studio", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/bTspnAQUqxwnsUNR.png" },
    { name: "Oak Grove Ventures", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/XRWdokHOjoXeeoBC.png" },
    { name: "Solana Foundation", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/dBplicKdAjLRiUGs.png" },
    { name: "Stacks Foundation", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/MSxDrGNtXgTLcdkm.png" },
    { name: "Bitcoin Frontier Fund", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/CRgMMJhwjESXgIIx.png" },
    { name: "Anamaly", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/QdinsozcuMCsOLch.png" },
    { name: "Trust Machines", src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/TCYTZPxwLwEZxXun.png" },
  ];

  /* ── gradient helpers ── */
  const hexToRgba = (hex: string, alpha: number = 1): string => {
    if (hex.startsWith('rgb')) {
      const match = hex.match(/[\d.]+/g);
      if (match && match.length >= 3) return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      return hex;
    }
    const c = hex.replace('#', '');
    return `rgba(${parseInt(c.substring(0, 2), 16)}, ${parseInt(c.substring(2, 4), 16)}, ${parseInt(c.substring(4, 6), 16)}, ${alpha})`;
  };

  const solidColor = backgroundColor;
  const transparentColor = hexToRgba(backgroundColor, 0);

  /* Each logo gets a fixed pixel width so Safari doesn't collapse them */
  const LOGO_W_MOBILE = 120; // px
  const LOGO_W_DESKTOP = 160; // px
  const GAP_MOBILE = 32; // px (px-4 × 2)
  const GAP_DESKTOP = 48; // px (px-6 × 2)

  const LogoItem = ({ logo }: { logo: typeof logos[number] }) => (
    <div
      className="flex-shrink-0 flex items-center justify-center"
      style={{
        width: `${LOGO_W_MOBILE}px`,
        paddingLeft: `${GAP_MOBILE / 2}px`,
        paddingRight: `${GAP_MOBILE / 2}px`,
      }}
    >
      <img
        src={logo.src}
        alt={logo.name}
        className="h-8 sm:h-10 lg:h-12 w-auto object-contain pointer-events-none select-none"
        style={{ filter: 'brightness(0)', maxWidth: 'none' }}
        loading="eager"
        draggable={false}
      />
    </div>
  );

  /* Render 3 copies to guarantee seamless looping on wide screens */
  const LogoSet = () => (
    <>
      {logos.map((logo, i) => (
        <LogoItem key={i} logo={logo} />
      ))}
    </>
  );

  return (
    <div className="relative w-full overflow-hidden py-6 lg:py-8">
      {/* Gradient fade — left */}
      <div
        className="absolute left-0 top-0 bottom-0 w-16 sm:w-20 lg:w-32 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to right, ${solidColor}, ${transparentColor})` }}
      />
      {/* Gradient fade — right */}
      <div
        className="absolute right-0 top-0 bottom-0 w-16 sm:w-20 lg:w-32 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to left, ${solidColor}, ${transparentColor})` }}
      />

      {/* Scrolling strip — 3 copies for seamless loop */}
      <div
        className="flex animate-marquee"
        style={{
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        <LogoSet />
        <LogoSet />
        <LogoSet />
      </div>
    </div>
  );
};

export default PressLogos;
